<script setup lang="ts">
import { CarrierList, PrimaryButton, useTranslation, ENGLISH_LOCALE, SPANISH_LOCALE, assertNever, SupportedLocale } from '@hfc/ui-core';
import { useUrlHelper } from '@/page/infrastructure/composition/useUrlHelper';
import TextAndImage from '@/page/infrastructure/components/TextAndImage.vue';
import CarriersCarousel from '@/page/infrastructure/components/CarriersCarousel.vue';
import WhatExchangeDoesEn from '@/page/infrastructure/components/coveredCalifornia/WhatExchangeDoesEn.vue';
import WhatExchangeDoesEs from '@/page/infrastructure/components/coveredCalifornia/WhatExchangeDoesEs.vue';
import QualifiedHealthPlanEn from '@/page/infrastructure/components/coveredCalifornia/QualifiedHealthPlanEn.vue';
import QualifiedHealthPlanEs from '@/page/infrastructure/components/coveredCalifornia/QualifiedHealthPlanEs.vue';
import QuestionsSection from '@/page/infrastructure/components/QuestionsSection.vue';
import WhenCanEnroll from '@/page/infrastructure/components/coveredCalifornia/questions/WhenCanEnroll.vue';
import EnrollOutsideOpenEnrollment from '@/page/infrastructure/components/coveredCalifornia/questions/EnrollOutsideOpenEnrollment.vue';
import HowToApply from '@/page/infrastructure/components/coveredCalifornia/questions/HowToApply.vue';
import AfterApplication from '@/page/infrastructure/components/coveredCalifornia/questions/AfterApplication.vue';
import ImmigrationStatus from '@/page/infrastructure/components/coveredCalifornia/questions/ImmigrationStatus.vue';
import ServicesSection from '@/page/infrastructure/components/ServicesSection.ce.vue';
import { QuestionTab } from '@/page/domain/QuestionTab';
import LicenseContent from '@/page/infrastructure/components/LicenseContent.vue';

const { getIaFQuoteUrl, getBaseUrl } = useUrlHelper();
const { t, locale } = useTranslation();

const currentLocale = locale.value as SupportedLocale;

const questions: Array<QuestionTab> = [
  { text: t('coveredCalifornia.questions.whenCanEnroll.tab'), component: WhenCanEnroll },
  { text: t('coveredCalifornia.questions.enrollOutside.tab'), component: EnrollOutsideOpenEnrollment },
  { text: t('coveredCalifornia.questions.howToApply.tab'), component: HowToApply },
  { text: t('coveredCalifornia.questions.afterApplication.tab'), component: AfterApplication },
  { text: t('coveredCalifornia.questions.immigration.tab'), component: ImmigrationStatus },
];

</script>
<template>

  <div class="flex flex-col max-w-7xl mx-auto px-6">
    <TextAndImage div-class="pt-16 lg:pb-0" content-direction="right">
      <template #text>
        <div class="flex flex-col justify-center space-y-6">
          <div class="text-center text-blue-500">
            <span class="text-6xl" v-html="t('coveredCalifornia.title')"></span>
            <br>
            <span class="text-4xl">{{ t('coveredCalifornia.subtitle') }}</span>
          </div>
          <p>{{ t('coveredCalifornia.intro') }}</p>
          <div class="grid grid-cols-4 lg:grid-cols-12 gap-4">
            <div class="flex flex-row col-span-4 items-center space-x-2 lg:space-x-4">
              <img src="/assets/img/coveredCalifornia/icon-price.svg" />
              <span class="text-lg font-semibold">{{ t('coveredCalifornia.benefits.lowestPrices') }}</span>
            </div>
            <div class="flex flex-row col-span-4 items-center space-x-2 lg:space-x-4">
              <img src="/assets/img/coveredCalifornia/icon-discount.svg" />
              <span class="text-lg font-semibold">{{ t('common.governmentDiscounts') }}</span>
            </div>
            <div class="flex flex-row col-span-4 items-center space-x-2 lg:space-x-4">
              <img src="/assets/img/coveredCalifornia/icon-enroll.svg" />
              <span class="text-lg font-semibold">{{ t('coveredCalifornia.benefits.easyEnrollment') }}</span>
            </div>
          </div>
          <div class="flex flex-row justify-center lg:justify-normal">
            <PrimaryButton :label="t('common.getMyFreeQuoteNow')" size="lg" :href="getIaFQuoteUrl()" />
          </div>
        </div>
      </template>
      <template #image>
        <div class="flex flex-1 justify-center">
          <img class="flex rounded" src="/assets/img/coveredCalifornia/hero-image.png">
        </div>
      </template>
    </TextAndImage>
  </div>

  <CarriersCarousel />

  <WhatExchangeDoesEn v-if="currentLocale === ENGLISH_LOCALE" />
  <WhatExchangeDoesEs v-else-if="currentLocale === SPANISH_LOCALE" />
  <template v-else>{{ assertNever(currentLocale) }}</template>

  <div class="flex flex-col max-w-7xl mx-auto px-6">
    <TextAndImage div-class="pt-16 lg:pb-0" content-direction="left">
      <template #text>
        <div class="flex flex-col justify-center space-y-6">
          <span class="text-2xl lg:text-3xl font-bold text-slate-700">{{ t('coveredCalifornia.mandate.title') }}</span>
          <p v-html="t('coveredCalifornia.mandate.p1', { acaUrl: getBaseUrl('/affordable-care-act') })"></p>
          <span class="text-xs">{{ t('coveredCalifornia.mandate.footnote') }}</span>
        </div>
      </template>
      <template #image>
        <div class="flex flex-1 flex-col justify-center lg:text-lg space-y-5 pb-16 lg:pb-0">
          <div class="flex items-center space-x-4">
            <div class="flex w-20 shrink-0 justify-center">
              <hfc-icon icon="stethoscope" class="text-cyan-500 text-5xl" />
            </div>
            <span class="text-cyan-600 font-bold">{{ t('coveredCalifornia.mandate.enrolled') }}</span>
          </div>
          <div class="flex items-center space-x-4">
            <div class="flex w-20 shrink-0 justify-center">
              <hfc-icon icon="calculator" class="text-cyan-500 text-5xl" />
            </div>
            <span class="text-cyan-600 font-bold">{{ t('coveredCalifornia.mandate.exemption') }}</span>
          </div>
          <div class="flex items-center space-x-4">
            <div class="flex w-20 shrink-0 justify-center">
              <hfc-icon icon="hand-holding-dollar" class="text-cyan-500 text-5xl" />
            </div>
            <span class="text-cyan-600 font-bold">{{ t('coveredCalifornia.mandate.penalty') }}</span>
          </div>
        </div>
      </template>
    </TextAndImage>

    <QualifiedHealthPlanEn v-if="currentLocale === ENGLISH_LOCALE" />
    <QualifiedHealthPlanEs v-else-if="currentLocale === SPANISH_LOCALE" />
    <template v-else>{{ assertNever(currentLocale) }}</template>
  </div>

  <QuestionsSection :tabs="questions" :show-license-content="false" />

  <div class="flex flex-col max-w-7xl mx-auto px-6">
    <TextAndImage div-class="pt-16 pb-0" content-direction="right">
      <template #text>
        <div class="flex flex-1 flex-col justify-center space-y-6">
          <p>{{ t('coveredCalifornia.licenseInfo') }}</p>
        </div>
      </template>
      <template #image>
        <div class="flex flex-1 justify-center pb-16 lg:pb-0">
          <carrier-logo :carrier-id="CarrierList.CovCalCertified" class="w-40 md:w-52 lg:w-72 fill-black" />
        </div>
      </template>
    </TextAndImage>
  </div>

  <div class="text-center">
    <div class="py-14 max-w-5xl mx-auto px-4 sm:px-6">
      <div class="text-2xl lg:text-3xl font-semibold text-slate-700 mb-6">{{ t('coveredCalifornia.groupInsurance.title') }}</div>
      <p v-html="t('coveredCalifornia.groupInsurance.p1', { groupUrl: getBaseUrl('/group-health-insurance') })"></p>
    </div>
  </div>
  <div class="max-w-7xl mx-auto md:px-6">
    <ServicesSection/>
  </div>
  <div class="max-w-7xl mx-auto md:px-6">
    <LicenseContent />
  </div>
</template>
